import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { WORKS, GENRES } from "@/mocks/works";
import WorkCard from "@/components/base/WorkCard";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function Genres() {
  usePageMeta({
    title: "韩漫题材分类｜玄幻、恋爱、动作、恐怖等韩漫大全",
    description:
      "按题材浏览人气韩漫大全：玄幻、冒险、动作、恋爱、奇幻、恐怖、武侠、现实等分类一网打尽，快速找到你喜欢的韩漫题材。",
    path: "/genres",
  });

  const [params, setParams] = useSearchParams();
  const [keyword, setKeyword] = useState(params.get("q") ?? "");
  const activeGenre = params.get("g");

  const setGenre = (g: string | null) => {
    const next = new URLSearchParams(params);
    if (g) next.set("g", g);
    else next.delete("g");
    setParams(next);
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (keyword.trim()) next.set("q", keyword.trim());
    else next.delete("q");
    setParams(next);
  };

  const list = useMemo(() => {
    let filtered = [...WORKS];
    if (activeGenre) filtered = filtered.filter((w) => w.genres.includes(activeGenre));
    if (keyword.trim()) {
      const q = keyword.trim().toLowerCase();
      filtered = filtered.filter(
        (w) =>
          w.title.toLowerCase().includes(q) ||
          w.original.toLowerCase().includes(q) ||
          w.genres.some((g) => g.includes(q)) ||
          w.tags.some((t) => t.includes(q))
      );
    }
    return filtered.sort((a, b) => b.popularity - a.popularity);
  }, [activeGenre, keyword]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-24 md:px-6 md:py-28">
      <nav className="mb-6 text-xs text-foreground-500" aria-label="面包屑">
        <ol className="flex flex-wrap items-center gap-1.5">
          <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
          <li><i className="ri-arrow-right-s-line"></i></li>
          <li aria-current="page" className="text-foreground-700">题材分类</li>
        </ol>
      </nav>

      <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="font-label text-xs font-semibold uppercase tracking-widest text-accent-600">Genres</p>
          <h1 className="mt-1 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">韩漫题材分类</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            按题材挑选韩漫，玄幻、恋爱、动作、恐怖一网打尽。也可以直接搜索作品名或关键词，快速定位你喜欢的韩漫。
          </p>
        </div>
        <form onSubmit={submitSearch} className="relative w-full lg:w-80" role="search" aria-label="搜索韩漫">
          <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-foreground-400"></i>
          <input
            type="search"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索作品名 / 题材 / 标签"
            aria-label="搜索韩漫"
            className="h-11 w-full rounded-full border border-background-200 bg-background-100/70 pl-10 pr-4 text-sm text-foreground-950 outline-none transition-colors focus:border-accent-300 focus:bg-background-50"
          />
        </form>
      </div>

      <div className="mt-7 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setGenre(null)}
          className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
            !activeGenre ? "border-accent-500 bg-accent-500 text-background-50" : "border-background-200 bg-background-100 text-foreground-600 hover:border-accent-300"
          }`}
        >
          全部题材
        </button>
        {GENRES.map((g) => (
          <button
            key={g.name}
            type="button"
            onClick={() => setGenre(g.name)}
            className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
              activeGenre === g.name ? "border-accent-500 bg-accent-500 text-background-50" : "border-background-200 bg-background-100 text-foreground-600 hover:border-accent-300"
            }`}
          >
            {g.name}
          </button>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-between gap-2 text-sm">
        <p className="text-foreground-600">
          {activeGenre ? (
            <>
              当前题材：<strong className="text-accent-700">{activeGenre}</strong>
            </>
          ) : (
            "全部作品"
          )}
          {keyword.trim() && (
            <>
              {" "}· 关键词：<strong className="text-accent-700">「{keyword.trim()}」</strong>
            </>
          )}
          {" "}共 <strong className="text-foreground-950">{list.length}</strong> 部
        </p>
        {list.length > 0 && <button type="button" onClick={() => { setKeyword(""); setGenre(null); }} className="text-xs text-primary-600 hover:text-primary-700">清除筛选</button>}
      </div>

      {list.length > 0 ? (
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {list.map((work) => (
            <WorkCard key={work.id} work={work} />
          ))}
        </div>
      ) : (
        <div className="mt-16 flex flex-col items-center rounded-xl border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
          <i className="ri-search-eye-line text-4xl text-foreground-400"></i>
          <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">没有找到匹配的韩漫</h2>
          <p className="mt-2 text-sm text-foreground-600">换个题材或关键词再试试吧。</p>
        </div>
      )}
    </div>
  );
}